// View 7: Service Setup (Vendor-specific fields)

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useOrderStore } from '@/stores/orderStore';
import { useFieldRules } from '@/hooks';
import { api } from '@/api/client';
import { submitCheckout } from '@/services/checkoutService';
import { track } from '@/utils/analytics';
import { logger } from '@/utils/logger';
import { assembleCheckoutData } from '@/utils/checkoutAssembly';
import { SSNInput, ShortDateInput, EmailConfirmationSection, TermsOfServiceSection } from '@/components/forms';
import type { AppFieldResponse, CheckoutStep, AppQuestion } from '@/types';

// Detect SSN-type fields from API response
// Note: Using 'as string' because API may return types not in our type definition
const isSSNField = (q: AppQuestion): boolean =>
  (q.Type as string) === 'ssn' ||
  (q.Type as string) === 'social' ||
  q.ApplicationFieldId.toLowerCase().includes('ssn') ||
  q.ApplicationFieldId.toLowerCase().includes('socialsecurity');

// Detect short date fields (MM/YY format) for credit card expiration
// Note: API may return 'shortDate' (capital D) so we check case-insensitively
const isShortDateField = (q: AppQuestion): boolean =>
  (q.Type as string)?.toLowerCase() === 'shortdate' ||
  q.ApplicationFieldId.toLowerCase().includes('expir') ||
  q.ApplicationFieldId.toLowerCase().includes('ccexp');

// Detect dropdown fields - API may return 'dropdown' instead of 'select'
const isDropdownField = (q: AppQuestion): boolean =>
  q.Type === 'select' || (q.Type as string) === 'dropdown';

// Detect state dropdown fields - API returns Type: 'state'
const isStateField = (q: AppQuestion): boolean =>
  (q.Type as string) === 'state';

type AppQuestionWithTypeValues = AppQuestion & {
  TypeValues?: string;
};

// US States for state dropdown (50 states + DC)
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

// Helper to parse TypeValues string into Options array
// API may return options as a comma-separated string in TypeValues (e.g., "Visa, MasterCard")
const getOptionsForQuestion = (q: AppQuestion): Array<{value: string; label: string}> => {
  if (q.Options && q.Options.length > 0) {
    return q.Options;
  }
  // Parse TypeValues if present (e.g., "Visa, MasterCard")
  const typeValues = (q as AppQuestionWithTypeValues).TypeValues;
  if (typeValues) {
    return typeValues.split(',').map((v: string) => {
      const trimmed = v.trim();
      return { value: trimmed, label: trimmed };
    });
  }
  return [];
};

export function ServiceSetupView() {
  const navigate = useNavigate();
  const { userId, address, personalInfo, propertyData, configuredPlans, selectedServices, onitPlan, onitSelected, setAppFields, setUploadFlags, setConfirmationId, setElectricityCompletionLink } = useOrderStore();
  const { evaluateField } = useFieldRules();
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [acceptedDocIds, setAcceptedDocIds] = useState<Set<string>>(new Set());
  const [emailConfirmed, setEmailConfirmed] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const defaultsAppliedRef = useRef(false); // Track if defaults have been applied

  // Fetch checkout steps
  const planIds = selectedServices.map(s => s.planId).sort().join(',');

  const { data: checkoutSteps, isLoading: isLoadingCheckoutSteps, isError: isCheckoutError } = useQuery({
    queryKey: ['checkoutSteps', userId, planIds],
    queryFn: async () => {
      const response = await api.get<CheckoutStep[]>(
        '/api/2turniton/checkout/steps',
        { userId: userId || undefined }
      );
      return response.data || [];
    },
    enabled: !!userId,
    staleTime: 0,
  });

  // Compute common field IDs for duplicate filtering
  // Fields in the common section (VendorName === null) should not repeat in vendor sections
  const commonFieldIds = useMemo(() => {
    const commonSection = checkoutSteps?.find(s => s.VendorName === null);
    return new Set(commonSection?.AppQuestions.map(q => q.ApplicationFieldId) || []);
  }, [checkoutSteps]);

  // Aggregate document upload flags from vendor steps (VendorName !== null)
  const uploadFlags = useMemo(() => {
    if (!checkoutSteps?.length) return { isDLUpload: false, isLeaseUpload: false, isOwnUpload: false };
    const vendorSteps = checkoutSteps.filter(step => step.VendorName !== null);
    return {
      isDLUpload: vendorSteps.some(s => s.IsDLUpload),
      isLeaseUpload: vendorSteps.some(s => s.IsLeaseUpload),
      isOwnUpload: vendorSteps.some(s => s.IsOwnUpload),
    };
  }, [checkoutSteps]);

  // Check if any document uploads are required (respect rent/own status)
  const isRenting = personalInfo?.rentOrOwn === 'Rent';
  const needsLeaseDoc = (isRenting && uploadFlags.isLeaseUpload) || (!isRenting && uploadFlags.isOwnUpload);
  const documentsRequired = uploadFlags.isDLUpload || needsLeaseDoc;

  // Collect all vendor documents that require acceptance
  const allVendorDocuments = useMemo(() => {
    if (!checkoutSteps?.length) return [];
    return checkoutSteps.flatMap(step => step.DocumentList || []);
  }, [checkoutSteps]);

  // Get visible questions for a step after applying field rules and duplicate filtering
  const getVisibleQuestions = useCallback((step: CheckoutStep, isCommonSection: boolean): AppQuestion[] => {
    let questions = step.AppQuestions;

    // Filter duplicates for non-common sections
    if (!isCommonSection) {
      questions = questions.filter(q => !commonFieldIds.has(q.ApplicationFieldId));
    }

    // Apply field rules - filter out skipped fields
    questions = questions.filter(q => {
      const result = evaluateField(q);
      return !result.skip;
    });

    return questions;
  }, [commonFieldIds, evaluateField]);

  // Apply default values from field rules when checkout steps load (once only)
  useEffect(() => {
    if (!checkoutSteps?.length || defaultsAppliedRef.current) return;

    const defaultValues: Record<string, string> = {};

    checkoutSteps.forEach(step => {
      step.AppQuestions.forEach(q => {
        const result = evaluateField(q);
        // Only set default if rule provides one
        if (result.defaultValue) {
          defaultValues[q.ApplicationFieldId] = result.defaultValue;
        }
      });
    });

    if (Object.keys(defaultValues).length > 0) {
      setFormValues(prev => ({ ...prev, ...defaultValues }));
    }
    defaultsAppliedRef.current = true;
  }, [checkoutSteps, evaluateField]);

  // Derive validation state from form values, terms, and checkout steps
  const isValid = useMemo(() => {
    // Email validation: if emailConfirmed is false, newEmail must be a valid email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailValid = emailConfirmed || (newEmail.trim() !== '' && emailRegex.test(newEmail));

    // Check all vendor documents are accepted
    const allDocsAccepted = allVendorDocuments.every(doc => acceptedDocIds.has(doc.Id));

    // Form cannot be valid before checkout steps have loaded
    if (!checkoutSteps?.length) {
      return false;
    }

    const allRequiredFilled = checkoutSteps.every((step) => {
      // Skip ONIT Home validation - it's auto-filled with square footage
      const isOnitHome = onitPlan?.vendorId && step.VendorId === onitPlan.vendorId;
      if (isOnitHome) return true;

      const isCommonSection = step.VendorName === null;
      const visibleQuestions = getVisibleQuestions(step, isCommonSection);

      return visibleQuestions
        .filter((q) => {
          // Only validate required fields that aren't skipped by rules
          const result = evaluateField(q);
          return (q.IsRequired || result.forceRequired) && !result.skip && !result.skipValidation;
        })
        .every((q) => formValues[q.ApplicationFieldId]?.trim());
    });
    return allRequiredFilled && termsAccepted && emailValid && allDocsAccepted;
  }, [formValues, termsAccepted, checkoutSteps, getVisibleQuestions, evaluateField, emailConfirmed, newEmail, allVendorDocuments, acceptedDocIds, onitPlan]);

  useEffect(() => {
    if (isCheckoutError) {
      track('checkout_error', { error_context: 'checkout_steps_load' });
    }
  }, [isCheckoutError]);

  const handleInputChange = useCallback((fieldId: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  // Handle vendor document acceptance
  const handleDocAcceptChange = useCallback((documentId: string, accepted: boolean) => {
    setAcceptedDocIds((prev) => {
      const next = new Set(prev);
      if (accepted) {
        next.add(documentId);
      } else {
        next.delete(documentId);
      }
      return next;
    });
  }, []);

  // Submit order directly without documents
  const submitOrderWithoutDocuments = async (appFields: AppFieldResponse[]) => {
    if (!userId || !address || !personalInfo) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const assembled = assembleCheckoutData({
        address,
        personalInfo,
        configuredPlans,
        selectedServices,
        appFields,
        onitPlan,
        onitSelected,
      });

      if (!assembled.ok) {
        logger.error({ area: 'service-setup' }, assembled.logMessage);
        setSubmitError(assembled.error);
        setIsSubmitting(false);
        return;
      }

      assembled.warnings.forEach((warning) => {
        logger.warn({ area: 'service-setup', userId }, warning, {
          serviceStartDateSelection: assembled.serviceStartDateSelection,
          selectedServices,
          configuredPlans,
        });
      });

      logger.info(
        { area: 'service-setup', userId },
        `Checkout state: dob=set, configuredPlans=${configuredPlans.length}, serviceStartDates=${Object.keys(assembled.serviceStartDateSelection).length}, appFields=${appFields.length}`,
        {
          serviceStartDateSelection: assembled.serviceStartDateSelection,
          selectedServices,
          configuredPlans,
          appFieldCount: appFields.length,
        }
      );

      // Submit with empty files since no documents are required
      const result = await submitCheckout(userId, assembled.checkoutData, {});

      if (result.success && result.orderId) {
        setConfirmationId(result.orderId);
        setElectricityCompletionLink(result.electricityCompletionLink || null);
        const serviceTypes = selectedServices.map(s => s.type);
        track('checkout_order_complete', {
          confirmation_id: result.orderId,
          zip: address?.zip,
          flow_type: useOrderStore.getState().flowType,
          services: serviceTypes,
          has_water: serviceTypes.includes('water'),
          has_electricity: serviceTypes.includes('electricity'),
          has_internet: serviceTypes.includes('internet'),
          total_services: serviceTypes.length,
          onit_selected: onitSelected,
          onit_available: !!onitPlan,
          source: 'checkout_submit',
        });
        navigate('/confirmation');
      } else {
        setSubmitError(result.error || 'Checkout failed. Please try again.');
        // Detailed error tracked in checkoutService via checkout_api_error
        track('checkout_error', {
          error_context: 'checkout_submit',
          error_message: result.error || 'Checkout failed',
        });
      }
    } catch (err) {
      logger.error({ area: 'service-setup' }, 'Error submitting checkout', err instanceof Error ? err : undefined);
      setSubmitError('An unexpected error occurred. Please try again.');
      track('checkout_error', {
        error_context: 'checkout_submit_exception',
        error_message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if field is filled for styling
  const isFieldFilled = (fieldId: string) => {
    return formValues[fieldId]?.trim().length > 0;
  };

  const handleContinue = async () => {
    // Build appFields array from formValues with vendorId mapping
    // Only include fields from steps that have a valid vendorId (required for backend)
    const appFields = checkoutSteps?.length
      ? checkoutSteps
          .filter((step) => step.VendorId) // Only include steps with valid vendorId
          .flatMap((step) =>
            step.AppQuestions
              .filter((q) => {
                // Include if has value in formValues
                if (formValues[q.ApplicationFieldId]?.trim()) return true;
                // Also include hidden fields with default values (auto-filled fields)
                const ruleResult = evaluateField(q);
                if (ruleResult.hide && ruleResult.defaultValue) return true;
                return false;
              })
              .map((q) => {
                // Use formValues if available, otherwise use rule's defaultValue
                const ruleResult = evaluateField(q);
                const value = formValues[q.ApplicationFieldId]?.trim() || ruleResult.defaultValue || '';
                return {
                  appFieldId: q.ApplicationFieldId,
                  appFieldResponse: value,
                  vendorId: step.VendorId!,
                };
              })
          )
      : [];

    // Auto-fill square footage fields for hidden vendor steps (e.g., ONIT Home)
    // Scans all steps — only fills fields not already populated from form input
    if (checkoutSteps?.length) {
      checkoutSteps
        .filter(step => step.VendorId)
        .forEach(step => {
          step.AppQuestions.forEach(question => {
            const alreadyFilled = appFields.some(
              f => f.appFieldId === question.ApplicationFieldId && f.vendorId === step.VendorId
            );
            if (!alreadyFilled) {
              const isSquareFootageField = question.Name.toLowerCase().includes('square feet') ||
                question.Name.toLowerCase().includes('sq ft') ||
                question.Name.toLowerCase().includes('sqft');

              if (isSquareFootageField) {
                const squareFootage = propertyData?.squareFootage || 2000;
                if (!propertyData?.squareFootage) {
                  // RentCast retired (Sep 2026) so this is now the normal path, not a warning
                  logger.info({ area: 'service-setup' }, `Auto-filling square footage with default (2000) for vendor ${step.VendorId}`);
                }
                appFields.push({
                  appFieldId: question.ApplicationFieldId,
                  appFieldResponse: String(squareFootage),
                  vendorId: step.VendorId!,
                });
              }
            }
          });
        });
    }

    // Fallback: if ONIT is selected but square footage wasn't populated (e.g., checkout steps
    // didn't include ONIT step due to timing), inject it silently with property data or default
    if (onitSelected && onitPlan?.vendorId) {
      const hasOnitSquareFootage = appFields.some(
        f => f.vendorId === onitPlan.vendorId &&
          f.appFieldId === 'DC674432-8899-4FD4-8EFB-E5AF9D1CD26F'
      );
      if (!hasOnitSquareFootage) {
        const squareFootage = propertyData?.squareFootage || 2000;
        logger.warn({ area: 'service-setup' }, `ONIT square footage missing from checkout steps — injecting fallback (${squareFootage} sqft)`);
        appFields.push({
          appFieldId: 'DC674432-8899-4FD4-8EFB-E5AF9D1CD26F',
          appFieldResponse: String(squareFootage),
          vendorId: onitPlan.vendorId,
        });
      }
    }

    setAppFields(appFields);

    const serviceTypes = selectedServices.map(s => s.type);
    track('checkout_steps_completed', {
      zip: address?.zip,
      flow_type: useOrderStore.getState().flowType,
      services: serviceTypes,
      total_services: serviceTypes.length,
      documents_required: documentsRequired,
    });

    if (documentsRequired) {
      setUploadFlags(uploadFlags);
      navigate('/documents');
    } else {
      // Submit order directly without documents
      await submitOrderWithoutDocuments(appFields);
    }
  };

  // Early return: loading state — no form, no buttons
  if (isLoadingCheckoutSteps) {
    return (
      <section className="view" style={{ display: 'block' }}>
        <div className="container">
          <h1 className="text--center mb-4">Almost there! Let&apos;s set up your selected utilities</h1>
          <div className="loading-container mb-4">
            <div className="spinner"></div>
            <p className="text--center">Loading your setup options...</p>
          </div>
        </div>
      </section>
    );
  }

  // Early return: error state
  if (isCheckoutError || !checkoutSteps?.length) {
    return (
      <section className="view" style={{ display: 'block' }}>
        <div className="container">
          <h1 className="text--center mb-4">Almost there! Let&apos;s set up your selected utilities</h1>
          <div className="error-notice mb-4">
            <p className="error-notice__text">
              {isCheckoutError
                ? 'We encountered an error loading your setup options. Please try again.'
                : 'No setup options were found. Please go back and try again.'}
            </p>
          </div>
          <div className="action-row">
            <button type="button" className="btn btn--back" onClick={() => navigate(-1)}>
              GO BACK
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="view" style={{ display: 'block' }}>
      <div className="container">
        <h1 className="text--center mb-4">Almost there! Let&apos;s set up your selected utilities</h1>

        {/* Dynamic checkout steps from API */}
        <div id="placeholder-checkout-content">
          {checkoutSteps.map((step, index) => {
            // Skip ONIT Home - it's handled separately (auto-filled with square footage)
            const isOnitHome = onitPlan?.vendorId && step.VendorId === onitPlan.vendorId;
            const isCommonSection = step.VendorName === null;

            if (isOnitHome) return null;

            // Get visible questions after applying field rules and duplicate filtering
            const visibleQuestions = getVisibleQuestions(step, isCommonSection);

            // Skip empty vendor sections after filtering
            if (visibleQuestions.length === 0) return null;

            return (
              <div
                key={step.VendorId || `common-${index}`}
                className="vendor-checkout-section mb-4"
                data-vendor-id={step.VendorId || 'common'}
              >
                {/* Vendor Header - skip for common section */}
                {!isCommonSection && (
                  <div className="vendor-checkout-header mb-4">
                    {step.VendorLogo && (
                      <img
                        src={step.VendorLogo}
                        alt={step.VendorName || ''}
                        className="vendor-checkout-header__logo"
                      />
                    )}
                    <h3 className="mb-0">{step.VendorName || `Provider ${index + 1}`}</h3>
                  </div>
                )}

                {/* General Information header for common section */}
                {isCommonSection && (
                  <div className="vendor-checkout-header mb-4">
                    <h3 className="mb-0">General Information</h3>
                  </div>
                )}

                {/* Email Confirmation Section - only in common section */}
                {isCommonSection && (
                  <EmailConfirmationSection
                    email={personalInfo?.email}
                    emailConfirmed={emailConfirmed}
                    onEmailConfirmedChange={setEmailConfirmed}
                    newEmail={newEmail}
                    onNewEmailChange={setNewEmail}
                  />
                )}

                {/* Form Fields - only render visible questions */}
                {visibleQuestions.map((question) => {
                  // Get rule result for this field
                  const ruleResult = evaluateField(question);

                  // Skip hidden fields (they're auto-filled but not shown)
                  if (ruleResult.hide) return null;

                  return (
                    <div key={question.ApplicationFieldId} className="form-section mb-4">
                      <label htmlFor={question.ApplicationFieldId} className="form-label">
                        <span>
                          {question.Name.toUpperCase()}
                          {(question.IsRequired || ruleResult.forceRequired) && !ruleResult.skipValidation && (
                            <span className={`required${isFieldFilled(question.ApplicationFieldId) ? ' filled' : ''}`}>
                              *
                            </span>
                          )}
                        </span>
                        {question.HelpText && (
                          <a href="#" className="form-label__help">
                            {question.HelpText}
                          </a>
                        )}
                      </label>
                      {isDropdownField(question) ? (
                        <select
                          id={question.ApplicationFieldId}
                          name={question.ApplicationFieldId}
                          className={`form-select${isFieldFilled(question.ApplicationFieldId) ? ' field-filled' : ''}`}
                          value={formValues[question.ApplicationFieldId] || ''}
                          onChange={(e) => handleInputChange(question.ApplicationFieldId, e.target.value)}
                          required={(question.IsRequired || ruleResult.forceRequired) && !ruleResult.skipValidation}
                        >
                          <option value="">{question.Placeholder || 'Select...'}</option>
                          {getOptionsForQuestion(question).map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : isStateField(question) ? (
                        <select
                          id={question.ApplicationFieldId}
                          name={question.ApplicationFieldId}
                          className={`form-select${isFieldFilled(question.ApplicationFieldId) ? ' field-filled' : ''}`}
                          value={formValues[question.ApplicationFieldId] || ''}
                          onChange={(e) => handleInputChange(question.ApplicationFieldId, e.target.value)}
                          required={(question.IsRequired || ruleResult.forceRequired) && !ruleResult.skipValidation}
                        >
                          <option value="">{question.Placeholder || question.Name}</option>
                          {US_STATES.map((state) => (
                            <option key={state.value} value={state.value}>
                              {state.label}
                            </option>
                          ))}
                        </select>
                      ) : isSSNField(question) ? (
                        <SSNInput
                          id={question.ApplicationFieldId}
                          name={question.ApplicationFieldId}
                          className={`form-input${isFieldFilled(question.ApplicationFieldId) ? ' field-filled' : ''}`}
                          placeholder={question.Placeholder || 'XXX-XX-XXXX'}
                          value={formValues[question.ApplicationFieldId] || ''}
                          onChange={(value) => handleInputChange(question.ApplicationFieldId, value)}
                          required={(question.IsRequired || ruleResult.forceRequired) && !ruleResult.skipValidation}
                        />
                      ) : isShortDateField(question) ? (
                        <ShortDateInput
                          id={question.ApplicationFieldId}
                          name={question.ApplicationFieldId}
                          className={`form-input${isFieldFilled(question.ApplicationFieldId) ? ' field-filled' : ''}`}
                          placeholder={question.Placeholder || 'MM/YY'}
                          value={formValues[question.ApplicationFieldId] || ''}
                          onChange={(value) => handleInputChange(question.ApplicationFieldId, value)}
                          required={(question.IsRequired || ruleResult.forceRequired) && !ruleResult.skipValidation}
                        />
                      ) : (
                        <input
                          type={question.Type || 'text'}
                          id={question.ApplicationFieldId}
                          name={question.ApplicationFieldId}
                          className={`form-input${isFieldFilled(question.ApplicationFieldId) ? ' field-filled' : ''}`}
                          placeholder={question.Placeholder}
                          maxLength={question.MaxLength}
                          value={formValues[question.ApplicationFieldId] || ''}
                          onChange={(e) => handleInputChange(question.ApplicationFieldId, e.target.value)}
                          required={(question.IsRequired || ruleResult.forceRequired) && !ruleResult.skipValidation}
                        />
                      )}
                    </div>
                  );
                })}

                {/* Vendor-specific Terms of Service */}
                {step.DocumentList && step.DocumentList.length > 0 && (
                  <TermsOfServiceSection
                    documents={step.DocumentList}
                    acceptedIds={acceptedDocIds}
                    onAcceptChange={handleDocAcceptChange}
                  />
                )}
              </div>
            );
          })}

          {/* Terms and Conditions */}
          <div className={`terms-highlight mb-4${termsAccepted ? ' terms-highlight--accepted' : ''}`}>
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="terms"
                className="checkbox-input"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
              />
              <span className="checkbox-text terms-highlight__text">
                I agree to 2TurnItOn&apos;s{' '}
                <a href="https://www.2turniton.com/terms-conditions" target="_blank" rel="noopener noreferrer">
                  Terms and Conditions
                </a>
              </span>
            </label>
          </div>
        </div>

        {/* Error Message */}
        {submitError && (
          <div className="error-notice mb-4">
            <p className="error-notice__text">{submitError}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-row">
          <button type="button" className="btn btn--back" onClick={() => navigate(-1)}>
            GO BACK
          </button>
          <button type="button" className="btn" onClick={handleContinue} disabled={!isValid || isSubmitting}>
            {isSubmitting ? 'SUBMITTING...' : 'SET UP MY SERVICE'}
          </button>
        </div>
      </div>
    </section>
  );
}
